<template>
  <div class="flex flex-col h-full text-white">
    <!-- Header -->
    <div class="mb-3">
      <h2 class="text-lg font-semibold">Send to Friend</h2>
      <p class="text-sm text-gray-400">
        {{ headerDescription }}
      </p>
    </div>

    <!-- Content preview -->
    <div v-if="review || title || list" class="p-3 mb-3 bg-gray-800 rounded-lg">
      <!-- Review preview -->
      <div v-if="review" class="flex gap-3 items-center">
        <div
          class="overflow-hidden w-16 h-12 bg-gray-700 rounded shrink-0"
          style="aspect-ratio: 4/3">
          <img
            v-if="reviewImageUrl"
            :src="reviewImageUrl"
            class="object-cover w-full h-full"
            alt="Review" />
          <div
            v-else-if="isLoadingReviewImage"
            class="flex justify-center items-center w-full h-full">
            <fa-icon icon="spinner-third" spin class="text-xs text-gray-500" />
          </div>
          <div
            v-else
            class="w-full h-full"
            :style="{ backgroundColor: reviewColor }"></div>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">{{ review.title_title }}</p>
          <p class="text-xs text-gray-400">Your review · {{ reviewScore }}</p>
        </div>
      </div>
      <!-- Title preview -->
      <div v-else-if="title" class="flex gap-3 items-center">
        <img
          v-if="title.poster_path"
          :src="`https://image.tmdb.org/t/p/w92${title.poster_path}`"
          class="object-cover w-12 h-16 rounded shrink-0" />
        <div v-else class="w-12 h-16 bg-gray-700 rounded shrink-0"></div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">
            {{ title.title || title.name }}
          </p>
          <p class="text-xs text-gray-400">{{ titleYear }}</p>
        </div>
      </div>
      <!-- List preview -->
      <div v-else-if="list" class="flex gap-3 items-center">
        <div
          class="flex justify-center items-center w-12 h-12 bg-gray-700 rounded shrink-0">
          <fa-icon :icon="['far', 'rectangle-list']" class="text-gray-400" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">{{ list.title }}</p>
          <p class="text-xs text-gray-400">
            {{ list.list_items?.[0]?.count || 0 }} items
          </p>
        </div>
      </div>
    </div>

    <!-- Optional message -->
    <div class="mb-3">
      <textarea
        v-model="message"
        placeholder="Add a message (optional)"
        rows="2"
        class="py-2 px-3 w-full text-sm placeholder-gray-500 text-white bg-gray-800 rounded-lg border border-gray-700 resize-none focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"></textarea>
    </div>

    <!-- Tabs -->
    <div class="flex gap-4 mb-3 border-b border-gray-800">
      <button
        :class="[
          'pb-2 text-sm transition-colors border-b-2 -mb-px',
          activeTab === 'suggested'
            ? 'text-white border-indigo-500'
            : 'text-gray-500 border-transparent hover:text-gray-300'
        ]"
        @click="activeTab = 'suggested'">
        Suggested
      </button>
      <button
        :class="[
          'pb-2 text-sm transition-colors border-b-2 -mb-px',
          activeTab === 'search'
            ? 'text-white border-indigo-500'
            : 'text-gray-500 border-transparent hover:text-gray-300'
        ]"
        @click="activeTab = 'search'">
        Search
      </button>
    </div>

    <!-- Tab content - scrollable area -->
    <div class="overflow-y-auto flex-1 px-2 pb-4 -mx-2 min-h-0">
      <!-- Suggested tab (People you follow) -->
      <div v-if="activeTab === 'suggested'">
        <!-- Loading state -->
        <div v-if="isLoadingFollowing" class="flex justify-center py-8">
          <fa-icon icon="spinner-third" spin class="text-2xl text-gray-500" />
        </div>

        <!-- Empty state -->
        <div
          v-else-if="followingUsers.length === 0"
          class="py-8 text-sm text-center text-gray-500">
          <fa-icon :icon="['far', 'user-group']" class="block mb-2 text-2xl" />
          <p>You're not following anyone yet</p>
          <p class="mt-1 text-xs">Follow people to see them here</p>
        </div>

        <!-- User list -->
        <div v-else class="space-y-2">
          <button
            v-for="user in followingUsers"
            :key="user.user_id"
            class="p-2 w-full text-left rounded-lg transition-colors"
            :class="
              isSelected(user)
                ? 'bg-indigo-600/20 ring-1 ring-indigo-500'
                : 'bg-gray-800 hover:bg-gray-700'
            "
            @click="toggleUser(user)">
            <UserBlock
              :user="user"
              size="md"
              :link-to-profile="false"
              :show-username="true">
              <template #right>
                <div
                  class="flex justify-center items-center rounded-full size-6 shrink-0"
                  :class="isSelected(user) ? 'bg-indigo-500' : 'bg-gray-700'">
                  <fa-icon
                    :icon="isSelected(user) ? 'check' : 'plus'"
                    class="text-xs text-white" />
                </div>
              </template>
            </UserBlock>
          </button>
        </div>
      </div>

      <!-- Search tab -->
      <div v-else-if="activeTab === 'search'">
        <!-- Search input -->
        <div class="relative mb-3">
          <fa-icon
            icon="search"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            placeholder="Search by username..."
            class="w-full pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" />
          <fa-icon
            v-if="isSearching"
            icon="spinner-third"
            spin
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
        </div>

        <!-- Search results -->
        <div v-if="searchQuery.length >= 2">
          <!-- Loading state -->
          <div v-if="isSearching" class="flex justify-center py-8">
            <fa-icon icon="spinner-third" spin class="text-2xl text-gray-500" />
          </div>

          <!-- Empty state -->
          <div
            v-else-if="searchResults.length === 0"
            class="py-8 text-sm text-center text-gray-500">
            <fa-icon icon="search" class="block mb-2 text-2xl" />
            <p>No users found for "{{ searchQuery }}"</p>
          </div>

          <!-- Results list -->
          <div v-else class="space-y-2">
            <button
              v-for="user in searchResults"
              :key="user.user_id"
              class="p-2 w-full text-left rounded-lg transition-colors"
              :class="
                isSelected(user)
                  ? 'bg-indigo-600/20 ring-1 ring-indigo-500'
                  : 'bg-gray-800 hover:bg-gray-700'
              "
              @click="toggleUser(user)">
              <UserBlock
                :user="user"
                size="md"
                :link-to-profile="false"
                :show-username="true">
                <template #right>
                  <div
                    class="flex justify-center items-center rounded-full size-6 shrink-0"
                    :class="isSelected(user) ? 'bg-indigo-500' : 'bg-gray-700'">
                    <fa-icon
                      :icon="isSelected(user) ? 'check' : 'plus'"
                      class="text-xs text-white" />
                  </div>
                </template>
              </UserBlock>
            </button>
          </div>
        </div>

        <!-- Initial state (no search query) -->
        <div v-else class="py-8 text-sm text-center text-gray-500">
          <fa-icon icon="search" class="block mb-2 text-2xl" />
          <p>Search for users by username</p>
          <p class="mt-1 text-xs">Type at least 2 characters</p>
        </div>
      </div>
    </div>

    <!-- Fixed footer -->
    <div
      class="px-6 pt-3 pb-6 -mx-6 -mb-6 bg-gray-900 border-t border-gray-800 shrink-0">
      <!-- Selected users -->
      <div v-if="selectedUsers.length > 0" class="mb-3">
        <div class="flex flex-wrap gap-2">
          <button
            v-for="user in selectedUsers"
            :key="user.user_id"
            class="flex items-center gap-1.5 px-2 py-1 bg-indigo-600 rounded-full text-xs hover:bg-indigo-700 transition-colors"
            @click="toggleUser(user)">
            <UserAvatar :user="user" size="4" />
            <span>@{{ user.username }}</span>
            <fa-icon icon="xmark" class="text-indigo-300 text-[10px]" />
          </button>
        </div>
      </div>

      <p v-if="sendError" class="mb-2 text-sm text-center text-red-400">
        {{ sendError }}
      </p>

      <button
        :disabled="selectedUsers.length === 0 || isSending"
        class="py-3 px-4 w-full text-sm font-semibold text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        @click="handleSend">
        <fa-icon v-if="isSending" icon="spinner-third" spin class="mr-2" />
        {{ sendButtonText }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreUsers } from '@/stores/storeUsers'
import { useStoreMessages } from '@/stores/storeMessages'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreReviews } from '@/stores/storeReviews'
import { useReviewColors } from '@/use/useReviewColors'
import UserAvatar from '@/components/UserAvatar.vue'
import UserBlock from '@/components/UserBlock.vue'

const props = defineProps({
  // Content to send - at least one should be provided
  review: {
    type: Object,
    default: null
  },
  title: {
    type: Object,
    default: null
  },
  list: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'sent'])

const storeProfile = useStoreProfile()
const storeUsers = useStoreUsers()
const storeMessages = useStoreMessages()
const storeAuth = useStoreAuth()
const storeReviews = useStoreReviews()
const { scoreHsl } = useReviewColors()

// Refs
const searchInput = ref(null)

// State
const activeTab = ref('suggested')
const searchQuery = ref('')
const message = ref('')
const selectedUsers = ref([])
const isSearching = ref(false)
const isLoadingFollowing = ref(false)
const isSending = ref(false)
const sendError = ref(null)
const searchResults = ref([])
const followingUsers = ref([])
const reviewImageUrl = ref(null)
const isLoadingReviewImage = ref(false)

// Debounce timer
let searchTimeout = null

// Computed
const headerDescription = computed(() => {
  if (props.review) return 'Share your review with friends'
  if (props.list) return 'Share this list with friends'
  return 'Recommend this title to friends'
})

const reviewScore = computed(() => {
  if (!props.review) return ''
  const score =
    props.review.score_overall ||
    (props.review.score_a +
      props.review.score_b +
      props.review.score_c +
      props.review.score_d +
      props.review.score_e +
      props.review.score_f) /
      6
  return score.toFixed(1)
})

const reviewColor = computed(() => {
  if (!props.review) return null
  return scoreHsl(parseFloat(reviewScore.value))
})

const loadReviewImage = async () => {
  if (!props.review?.id) return

  // Skip API call if no image_transform exists - no custom image was created
  if (!props.review?.image_transform) {
    reviewImageUrl.value = null
    return
  }

  isLoadingReviewImage.value = true
  try {
    const url = await storeReviews.getReviewImageUrl(props.review.id, 'plain')
    reviewImageUrl.value = url
  } catch (error) {
    console.error('Error loading review image:', error)
  } finally {
    isLoadingReviewImage.value = false
  }
}

const titleYear = computed(() => {
  if (!props.title) return ''
  const date = props.title.release_date || props.title.first_air_date
  if (!date) return ''
  return new Date(date).getFullYear()
})

const sendButtonText = computed(() => {
  if (isSending.value) return 'Sending...'
  if (selectedUsers.value.length === 0) return 'Select users to send'
  if (selectedUsers.value.length === 1) {
    return `Send to @${selectedUsers.value[0].username}`
  }
  return `Send to ${selectedUsers.value.length} people`
})

// Methods
const isSelected = (user) => {
  return selectedUsers.value.some((u) => u.user_id === user.user_id)
}

const toggleUser = (user) => {
  if (isSelected(user)) {
    selectedUsers.value = selectedUsers.value.filter(
      (u) => u.user_id !== user.user_id
    )
  } else {
    selectedUsers.value = [...selectedUsers.value, user]
  }
}

const searchUsers = async (query) => {
  if (!query || query.length < 2) {
    searchResults.value = []
    return
  }

  isSearching.value = true
  try {
    await storeUsers.searchUsers(query)
    // Filter out current user from results
    searchResults.value = storeUsers.searchResults.filter(
      (u) => u.user_id !== storeAuth.user?.id
    )
  } catch (error) {
    console.error('Error searching users:', error)
    searchResults.value = []
  } finally {
    isSearching.value = false
  }
}

const loadFollowing = async () => {
  isLoadingFollowing.value = true
  try {
    // Use the follows from storeProfile (users the current user follows)
    await storeProfile.fetchUserFollows(storeAuth.user?.id)
    followingUsers.value = storeProfile.follows.map(
      (rel) => rel.profiles || rel
    )
  } catch (error) {
    console.error('Error loading following:', error)
    followingUsers.value = []
  } finally {
    isLoadingFollowing.value = false
  }
}

const handleSend = async () => {
  if (selectedUsers.value.length === 0) return

  isSending.value = true
  sendError.value = null
  try {
    const recipientIds = selectedUsers.value.map((u) => u.user_id)

    // The title prop may be a TMDB search result (numeric id) or an internal
    // titles row (uuid id, tmdb_id column) - only ever send a real TMDB id
    const tmdbId =
      props.title?.tmdb_id ??
      (typeof props.title?.id === 'number' ? props.title.id : null)
    const tmdbType =
      props.title?.tmdb_type ||
      props.title?.media_type ||
      (props.title?.first_air_date
        ? 'tv'
        : props.title?.release_date
          ? 'movie'
          : null)

    await storeMessages.sendToUsers({
      recipientIds,
      tmdbId,
      tmdbType,
      reviewId: props.review?.id || null,
      listId: props.list?.id || null,
      message: message.value.trim() || null
    })

    emit('sent', { recipients: selectedUsers.value })
    emit('close')
  } catch (error) {
    console.error('Error sending:', error)
    sendError.value = 'Could not send. Check your connection and try again.'
  } finally {
    isSending.value = false
  }
}

// Watch for tab changes to focus search input
watch(activeTab, async (newTab) => {
  if (newTab === 'search') {
    await nextTick()
    searchInput.value?.focus()
  }
})

// Watch search query with debounce
watch(searchQuery, (newQuery) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  if (!newQuery || newQuery.length < 2) {
    searchResults.value = []
    isSearching.value = false
    return
  }

  isSearching.value = true
  searchTimeout = setTimeout(() => {
    searchUsers(newQuery)
  }, 300)
})

// Load data on mount
onMounted(() => {
  loadFollowing()
  if (props.review) {
    loadReviewImage()
  }
})
</script>
